// @story HIR-HR21
/* "Something wasn't right" on an interview brief or an interview report
 * (design "Interview briefs & guardrails", 30 Oct 2026: feedback, not
 * editing). One plain question, free text, sent to
 * POST /api/interview-briefs/feedback. Nothing changes by itself: a brief
 * problem is for the school's next version; a behaviour problem goes to
 * EquaOS, which changes the base prompt for every school.
 *
 *   <SomethingWrong about="report" reportId={report._id} />
 *   <SomethingWrong about="brief" briefId={brief.briefId} />
 */
import React, { useState } from 'react';
import api from '../../services/api';
import { Button, Input } from '../../ds';

const SENT_WORDS = {
  report: 'Thank you. Your note is with EquaOS, with this report attached. If the AI interviewer behaved wrongly, EquaOS fixes it for every school.',
  brief: 'Thank you. Your note is kept with this brief. To change the brief itself, edit it and send the new version for approval.',
};

export default function SomethingWrong({ about, briefId, reportId, className }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const id = `something-wrong-${about}-${briefId || reportId}`;

  if (!briefId && !reportId) return null;

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post('/interview-briefs/feedback', { about, text: text.trim(), ...(about === 'brief' ? { briefId } : { reportId }) });
      setSent(true);
      setOpen(false);
      setText('');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not send it just now. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className={className} style={{ display: 'grid', gap: 8 }}>
      {!open && (
        <div>
          <Button size="sm" variant="ghost" onClick={() => { setOpen(true); setSent(false); }}>Something wasn't right</Button>
        </div>
      )}
      {sent && <p role="status" style={{ margin: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>{SENT_WORDS[about]}</p>}
      {open && (
        <div role="dialog" aria-label="Something wasn't right" style={{ display: 'grid', gap: 8, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12 }}>
          <Input id={id} label="What went wrong?" multiline rows={3} value={text} onChange={(e) => setText(e.target.value)}
            hint={about === 'report' ? 'For example: it asked something it should not have, or the score does not match the answer.' : 'For example: a question is unclear, or a rubric line does not fit this role.'} />
          {error && <p role="alert" style={{ margin: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--status-danger-text)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="sm" onClick={send} loading={busy} disabled={!text.trim() || busy}>Send</Button>
            <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setError(''); }} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
